import { useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  SparklesIcon,
  CogIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

/**
 * BrandSettingsOnboardingModal Component
 *
 * Appears after Instagram account OAuth connection to guide users
 * to configure brand settings (tone of voice, language, emojis).
 *
 * Only shown if account has NO tone-of-voice examples configured.
 *
 * @param {boolean} isOpen - Modal visibility state
 * @param {function} onClose - Callback to close modal
 * @param {string} accountId - Instagram account ID
 * @param {string} accountUsername - Instagram username (for analytics)
 */
const BrandSettingsOnboardingModal = ({
  isOpen,
  onClose,
  accountId,
  accountUsername
}) => {
  const { t } = useTranslation('settings')
  const navigate = useNavigate()
  const modalRef = useRef(null)
  const modalOpenTime = useRef(null)

  // All hooks MUST be called before any early returns
  const handleDismiss = useCallback((method) => {
    if (window.gtag) {
      window.gtag('event', 'brand_onboarding_dismissed', {
        account_id: accountId,
        account_username: accountUsername,
        method
      })
    }
    onClose()
  }, [accountId, accountUsername, onClose])

  // Prevent scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      modalOpenTime.current = Date.now()

      if (window.gtag) {
        window.gtag('event', 'brand_onboarding_modal_shown', {
          account_id: accountId,
          account_username: accountUsername
        })
      }
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, accountId, accountUsername])

  // Keyboard support (ESC to close)
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleDismiss('escape_key')
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, handleDismiss])

  // Focus management
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const firstButton = modalRef.current.querySelector('button')
      firstButton?.focus()
    }
  }, [isOpen])

  // Early return AFTER all hooks
  if (!isOpen) return null

  const handleConfigureBrandSettings = () => {
    if (window.gtag) {
      window.gtag('event', 'brand_onboarding_cta_clicked', {
        account_id: accountId,
        account_username: accountUsername,
        time_to_click_ms: modalOpenTime.current ? Date.now() - modalOpenTime.current : null
      })
    }
    navigate(`/accounts/${accountId}/brand-settings`)
    onClose()
  }

  const handleSkip = () => {
    if (window.gtag) {
      window.gtag('event', 'brand_onboarding_skipped', {
        account_id: accountId,
        account_username: accountUsername
      })
    }
    onClose()
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={() => handleDismiss('overlay_click')}
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-modal-title"
      aria-describedby="onboarding-modal-description"
    >
      <div
        ref={modalRef}
        className="relative max-w-2xl w-full mx-4 sm:mx-auto bg-white
                   rounded-2xl shadow-2xl border-2 border-purple-100
                   p-6 sm:p-8 space-y-4 sm:space-y-6
                   max-h-[90vh] overflow-y-auto
                   animate-in fade-in zoom-in duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button (X) - top right */}
        <button
          onClick={() => handleDismiss('close_button')}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600
                     transition-colors rounded-lg hover:bg-gray-100"
          aria-label={t('common:close')}
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        {/* HEADER SECTION */}
        <div className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-500
                          rounded-2xl flex items-center justify-center shadow-lg relative">
            <SparklesIcon className="w-8 h-8 text-white" />
            <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-white
                            rounded-full flex items-center justify-center">
              <CogIcon className="w-5 h-5 text-purple-600" />
            </div>
          </div>

          <h2
            id="onboarding-modal-title"
            className="text-2xl sm:text-3xl font-bold text-gray-900"
          >
            {t('accounts.onboarding.headline')}
          </h2>
        </div>

        {/* EXPLANATION SECTION */}
        <div className="space-y-4">
          <p
            id="onboarding-modal-description"
            className="text-base sm:text-lg text-gray-700 text-center leading-relaxed"
          >
            {t('accounts.onboarding.description')}
          </p>

          {/* Benefits List */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 space-y-4">
            {[1, 2, 3].map((num) => (
              <div key={num} className="flex items-start space-x-3">
                <CheckCircleIcon className="w-6 h-6 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900">
                    {t(`accounts.onboarding.benefit${num}.title`)}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {t(`accounts.onboarding.benefit${num}.description`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* VISUAL PREVIEW - hidden on mobile */}
        <div className="hidden sm:block bg-gray-100 rounded-xl p-6 border-2 border-dashed border-gray-300">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-gray-300 rounded-lg mb-2 flex items-center justify-center">
                <XMarkIcon className="w-10 h-10 text-gray-500" />
              </div>
              <p className="text-xs text-gray-500">
                {t('accounts.onboarding.without')}
              </p>
            </div>

            <ArrowRightIcon className="w-6 h-6 text-purple-600 transform sm:rotate-0 rotate-90" />

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-500
                              rounded-lg mb-2 flex items-center justify-center">
                <SparklesIcon className="w-10 h-10 text-white" />
              </div>
              <p className="text-xs text-gray-700 font-semibold">
                {t('accounts.onboarding.with')}
              </p>
            </div>
          </div>
        </div>

        {/* ACTION BUTTONS */}
        <div className="space-y-3">
          <button
            onClick={handleConfigureBrandSettings}
            className="w-full py-4 px-6 bg-gradient-to-r from-purple-600 to-pink-500
                       hover:from-purple-700 hover:to-pink-600 text-white font-semibold
                       rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02]
                       transition-all duration-200 flex items-center justify-center space-x-2"
          >
            <CogIcon className="w-5 h-5" />
            <span>{t('accounts.onboarding.cta')}</span>
            <ArrowRightIcon className="w-5 h-5" />
          </button>

          <button
            onClick={handleSkip}
            className="w-full py-3 px-6 text-gray-600 hover:text-gray-900 font-medium
                       transition-colors duration-200"
          >
            {t('accounts.onboarding.skip')}
          </button>

          <p className="text-xs text-center text-gray-500 italic">
            {t('accounts.onboarding.disclaimer')}
          </p>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default BrandSettingsOnboardingModal
